// Typed fetch helpers for /api/lobby/*. Mirrors the discipline in
// game/soloApi.ts (ApiResult envelope, credentials: 'include').

import type {
  CreateLobbyRequest,
  JoinLobbyRequest,
  LobbyStatePublic,
  MultiStateResponse,
} from '@origami/shared'
import { type ApiResult } from '../game/playerApi'

async function asResult<T>(res: Response): Promise<ApiResult<T>> {
  if (res.ok) return { ok: true, value: (await res.json()) as T }
  let code = 'unknown'
  let message: string | undefined
  try {
    const body = (await res.json()) as { error?: { code?: string; message?: string } }
    code = body?.error?.code ?? code
    message = body?.error?.message
  } catch {}
  return { ok: false, code, message, status: res.status }
}

async function asVoidResult(res: Response): Promise<ApiResult<void>> {
  if (res.ok) return { ok: true, value: undefined }
  let code = 'unknown'
  let message: string | undefined
  try {
    const body = (await res.json()) as { error?: { code?: string; message?: string } }
    code = body?.error?.code ?? code
    message = body?.error?.message
  } catch {}
  return { ok: false, code, message, status: res.status }
}

async function post<T>(url: string, body: object = {}): Promise<ApiResult<T>> {
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return asResult<T>(res)
}

async function postVoid(url: string, body: object = {}): Promise<ApiResult<void>> {
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return asVoidResult(res)
}

export const createLobby = (body: CreateLobbyRequest) =>
  post<MultiStateResponse>('/api/lobby/create', body)

export const joinLobby = (body: JoinLobbyRequest) =>
  post<MultiStateResponse>('/api/lobby/join', body)

export const leaveLobby = () => postVoid('/api/lobby/leave', {})

export const startMatch = () => postVoid('/api/lobby/start', {})

export const previewLobby = async (
  code: string,
): Promise<ApiResult<{ lobby: LobbyStatePublic }>> => {
  const res = await fetch(`/api/lobby/${encodeURIComponent(code)}`, {
    credentials: 'include',
  })
  return asResult<{ lobby: LobbyStatePublic }>(res)
}
